import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { FC, PropsWithChildren, ReactNode } from "react";
import { getDataAutomationGroup } from "@/helpers";
import { XMarkThick } from "@/icons";

export type DialogModalProps = {
  open: boolean;
  onClose: () => void;
  maxWidth?: "3xl" | "4xl" | "5xl" | "6xl" | "7xl";
  panelClassName?: string;
  renderCloseButton?: (onClose: () => void) => ReactNode;
};

const MAX_WIDTH_CLASSES = {
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
};

/**
 * DialogModal - Purpose-built for data-heavy dialogs with custom layouts
 * For standard marketing modals, use Modal component instead
 */
const DialogModal: FC<PropsWithChildren<DialogModalProps>> = ({
  open,
  onClose,
  maxWidth = "5xl",
  panelClassName,
  renderCloseButton,
  children,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="relative z-60"
      {...getDataAutomationGroup("dialog")}
    >
      <DialogBackdrop
        transition
        aria-hidden="true"
        className="bg-swdc-black/30 fixed inset-0 transition-opacity duration-150 data-closed:opacity-0"
      />
      <div className="fixed inset-0 flex h-[-webkit-fill-available] md:h-auto md:overflow-y-auto md:px-4 md:py-8">
        <DialogPanel
          transition
          className={`relative m-auto flex h-max w-full ${maxWidth ? MAX_WIDTH_CLASSES[maxWidth] : ""} flex-col bg-white p-14 pb-16 transition-opacity duration-150 data-closed:opacity-0 ${panelClassName || ""}`}
          {...getDataAutomationGroup("modal")}
        >
          {renderCloseButton ? (
            renderCloseButton(onClose)
          ) : (
            <button
              className="absolute top-4 right-4 max-w-max"
              onClick={onClose}
              aria-label="Close Dialog"
            >
              <XMarkThick
                width={27}
                height={27}
                className="text-swdc-gray-400 mr-1"
              />
            </button>
          )}
          {children}
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default DialogModal;
